/* istanbul ignore file */
// TODO [LEARN-15811]: ^ ignore coverage on this file until we can have app test scaffolding ready
import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Grommet } from 'grommet'
import { TabButton, TabSet } from 'secondstep-components'

import { launchPresentation } from 'utils/playerHelper'
import HsIndividualResourceActivityDetails from 'components/HsIndividualResourceActivityDetails'
import HsIndividualResourceMetadata from 'components/HsIndividualResourceMetadata'
import HsIndividualResourceReferences from 'components/HsIndividualResourceReferences'
import HsIndividualResourceMarkComplete from 'components/HsIndividualResourceMarkComplete'
import CoursePreviewBanner from 'components/CoursePreviewBanner'
import HighSchoolPreviewBanner from 'components/HighSchoolPreviewBanner'
import TieredInterventionMarkComplete from 'components/TieredInterventionMarkComplete'
import BackLink from 'components/BackLink'
import {
  getAudienceMetadata,
  getGradeMetadata,
  getTypeMetadata,
} from 'utils/highschoolHelpers'
import {
  extractGradeValue,
  getBreadcrumbPath,
  getBreadcrumbText,
} from './helpers'
import { getLanguageToggle } from 'components/HsIndividualResourceActivityDetails/helpers'
import { isResourceComplete } from 'utils/resourceCompletionHelpers'
import {
  getCurrentProgram,
  PROGRAM_TYPES,
  getProgramTheme,
} from 'utils/programHelpers'
import {
  tabTheme,
  ActivityButton,
  AudienceTagContainer,
  ControlPanel,
  DetailsContainer,
  ExternalLinkIcon,
  HeaderContainer,
  PageContainer,
  StyledHeaderOne,
  SurveyLink,
  BannerSpacer,
  SiteSelectorWrapper,
  HsIndividualResourceMarkCompleteWrapper,
  HeaderTitleContainer,
  TitleWrapper,
  HeaderContentWrapper,
  MetadataContainer,
  ResourceContainer,
  BreadcrumbWrapper,
  NavigationWrapper,
  StyledMarkdownParagraph,
  ResourceImageContainer,
  PreviewBannerWrapper,
} from './component.styles'
import { SURVEY_TEXT, LAUNCH_BUTTON_TEXT, TIER_2_TEXT } from './constants'
import externalLink from './assets/external_link.svg'
import SiteSelector from 'components/SiteSelector'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContextLegacy'

const IndividualResource = ({
  entry,
  gradeInstructorData,
  userContext,
  navigationEvent,
  isHsPreview,
  isPreview,
  productTabTitle,
  match,
  routeContent,
}) => {
  if (!entry) {
    return null
  }
  const {
    activityDetails,
    facetMetadataSetting,
    image,
    // rename this so it sounds less like a function:
    markComplete: markCompleteContent,
    presentation,
    references,
    resourceMetadata,
    surveyLink,
    languageToggle: entryLanguageToggle,
  } = entry || {}

  const { changeLocale, locale: routeLocale } = routeContent || {}
  const { metadataValues } = resourceMetadata || {}
  const [activeActivityIndex, setActiveActivityIndex] = useState(0)
  const [reference, setReference] = useState()
  const [openAccordionReference, setOpenAccordionReference] = useState(false)
  const supRef = useRef()
  const scrollToBottom = () => {
    setTimeout(() => supRef.current.scrollIntoView({ behavior: 'smooth' }), 500)
  }

  const { description, displayTitle, courseNodeConnector } =
    resourceMetadata || {}
  const { courseNodeEntryPoint: courseNodeId } = courseNodeConnector || {}
  const audience = getAudienceMetadata(resourceMetadata)
  const grades = getGradeMetadata(resourceMetadata)
  const type = getTypeMetadata(resourceMetadata)
  const currentProgram = getCurrentProgram()
  const isHighSchool = currentProgram === PROGRAM_TYPES.HIGH_SCHOOL

  const handleReferences = refClicked => {
    const referenceClicked = refClicked.replace(/[^\w\s]/gi, '')
    if (refClicked && referenceClicked) {
      setOpenAccordionReference(true)
      setReference(referenceClicked)
      scrollToBottom()
    }
  }

  const handleActivityTabs = activityIndex => {
    if (openAccordionReference) {
      setOpenAccordionReference(!openAccordionReference)
    }

    setReference(null)
    setActiveActivityIndex(activityIndex)
  }

  const tabTitles = {
    page: productTabTitle,
    tab: displayTitle,
  }
  useEffect(() => {
    if (displayTitle) {
      document.title = `${tabTitles.page} | ${tabTitles.tab}`
    }
  }, [displayTitle])

  const launchActivity = (isPreview = false) => {
    const { id } = presentation
    const theme = getProgramTheme()
    const locale = 'en-US'
    const mode = isPreview ? 'preview' : ''
    const lessonTitle = resourceMetadata?.routeSlug
    const extraQueryParams = isPreview ? '&tier=2' : ''
    const siteId = userContext?.sites?.[0]?.siteId
    launchPresentation({
      entryId: id,
      locale,
      mode,
      navigationEvent,
      theme,
      lessonTitle,
      extraQueryParams,
      userContext,
      siteId,
    })
  }

  const shouldShowMarkCompleteHs = ({ markCompleteContent, isPreview }) => {
    if (isPreview) {
      return false
    }
    if (!markCompleteContent) {
      return false
    }
    // Only show mark complete for high school for now
    if (currentProgram !== PROGRAM_TYPES.HIGH_SCHOOL) {
      return false
    }
    return true
  }

  const shouldShowTieredInterventionsMarkComplete = ({ isPreview }) => {
    if (isPreview) {
      return false
    }
    // Show mark complete for non-high school cases
    if (currentProgram === PROGRAM_TYPES.HIGH_SCHOOL) {
      return false
    }
    return true
  }

  const gradeValue = extractGradeValue(metadataValues)

  // Create breadcrumb navigation
  const handleBreadcrumbClick = () => {
    if (navigationEvent) {
      const breadcrumbPath = getBreadcrumbPath(currentProgram)
      navigationEvent(breadcrumbPath)
    }
  }

  // Create breadcrumb text based on current program
  const breadcrumbText = getBreadcrumbText(currentProgram, productTabTitle)

  return (
    <PageContainer>
      {isHsPreview && (
        <PreviewBannerWrapper>
          <BannerSpacer />
          <HighSchoolPreviewBanner />
        </PreviewBannerWrapper>
      )}
      {isPreview && !isHsPreview && (
        <PreviewBannerWrapper>
          <BannerSpacer />
          <CoursePreviewBanner />
        </PreviewBannerWrapper>
      )}

      {!isPreview && (
        <NavigationWrapper>
          {!isHighSchool && (
            <BreadcrumbWrapper>
              <BackLink dataTestId="breadcrumb" onClick={handleBreadcrumbClick}>
                {breadcrumbText}
              </BackLink>
            </BreadcrumbWrapper>
          )}
          <SiteSelectorWrapper isHighSchool={isHighSchool}>
            <SiteSelector />
          </SiteSelectorWrapper>
        </NavigationWrapper>
      )}
      <HeaderContainer>
        <div className="content">
          <HeaderContentWrapper isHighSchool={isHighSchool}>
            {isHighSchool ? (
              <AudienceTagContainer
                audience={audience}
                isHighSchool={isHighSchool}
              >
                {!!type && <div>{type}</div>}
                {!!grades && grades.length && <div>{grades}</div>}
              </AudienceTagContainer>
            ) : (
              <AudienceTagContainer
                audience={audience}
                isHighSchool={isHighSchool}
              >
                <div>{TIER_2_TEXT}</div>
                {!!gradeValue && <div>{gradeValue.toUpperCase()}</div>}
              </AudienceTagContainer>
            )}
            <HeaderTitleContainer>
              <TitleWrapper>
                <StyledHeaderOne>{displayTitle}</StyledHeaderOne>
              </TitleWrapper>
              {shouldShowTieredInterventionsMarkComplete({
                markCompleteContent,
                isPreview,
              }) && (
                <TieredInterventionMarkComplete
                  courseNodeId={courseNodeId}
                  instructorData={gradeInstructorData}
                  isComplete={isResourceComplete({
                    instructorData: gradeInstructorData,
                    courseNodeId,
                    program: currentProgram,
                  })}
                  match={match}
                  program={currentProgram}
                  userContext={userContext}
                />
              )}
            </HeaderTitleContainer>
            <StyledMarkdownParagraph small>
              {description}
            </StyledMarkdownParagraph>
            {!!surveyLink && (
              <SurveyLink
                href={surveyLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                {SURVEY_TEXT}
                <ExternalLinkIcon
                  alt={'External Link Icon'}
                  src={externalLink}
                />
              </SurveyLink>
            )}
            {shouldShowMarkCompleteHs({ markCompleteContent, isPreview }) && (
              <HsIndividualResourceMarkCompleteWrapper>
                <HsIndividualResourceMarkComplete
                  // 'audience' is a very overloaded term, but in this particular component it means which type activity this is
                  activityType={audience}
                  courseNodeId={courseNodeId}
                  markCompleteContent={markCompleteContent}
                  practiceName={displayTitle}
                />
              </HsIndividualResourceMarkCompleteWrapper>
            )}
          </HeaderContentWrapper>
          {isHighSchool && (
            <ResourceImageContainer
              alt={image?.description}
              className="resource-image"
              src={image?.url}
            />
          )}
        </div>
      </HeaderContainer>
      <ResourceContainer isHighSchool={isHighSchool}>
        <MetadataContainer isHighSchool={isHighSchool}>
          <HsIndividualResourceMetadata
            audience={audience}
            facetMetadataSetting={facetMetadataSetting}
            program={currentProgram}
            resourceMetadata={resourceMetadata}
          />
        </MetadataContainer>
        {isHighSchool && (
          <ControlPanel>
            <div className="content">
              {/* override the theme for tab buttons: */}
              <Grommet theme={tabTheme}>
                <TabSet>
                  {activityDetails.map((activity, index) => (
                    <TabButton
                      dataTestId={`${activity.displayTitle
                        .toLowerCase()
                        .replace(' ', '-')}-tab`}
                      isActive={index === activeActivityIndex}
                      key={`${activity.displayTitle.toLowerCase()}-${index}`}
                      label={activity.displayTitle}
                      onClick={() => handleActivityTabs(index)}
                    />
                  ))}
                </TabSet>
              </Grommet>
              {!!presentation && (
                <ActivityButton onClick={() => launchActivity(isPreview)}>
                  {LAUNCH_BUTTON_TEXT}
                </ActivityButton>
              )}
            </div>
          </ControlPanel>
        )}
        <DetailsContainer audience={audience} isHighSchool={isHighSchool}>
          <div className="content">
            {!!activityDetails[activeActivityIndex] && (
              <HsIndividualResourceActivityDetails
                audience={audience}
                details={activityDetails[activeActivityIndex]}
                getEntryWithNewLocale={changeLocale}
                isPreview={isPreview}
                languageToggle={
                  getLanguageToggle(activityDetails[activeActivityIndex]) ||
                  entryLanguageToggle ||
                  null
                }
                launchActivity={launchActivity}
                locale={routeLocale}
                presentation={presentation}
                program={currentProgram}
                referenceHandler={handleReferences}
              />
            )}
            {!!references && (
              <HsIndividualResourceReferences
                audience={audience}
                focusedReference={reference}
                isOpen={openAccordionReference}
                ref={supRef}
                references={references}
                setAccordionCollapse={setOpenAccordionReference}
              />
            )}
          </div>
        </DetailsContainer>
      </ResourceContainer>
    </PageContainer>
  )
}

IndividualResource.propTypes = {
  entry: PropTypes.object,
  gradeInstructorData: PropTypes.object,
  instructorData: PropTypes.object,
  isHsPreview: PropTypes.bool,
  isPreview: PropTypes.bool,
  match: PropTypes.func,
  navigationEvent: PropTypes.func,
  productTabTitle: PropTypes.string,
  routeContent: PropTypes.object,
  userContext: PropTypes.object,
}

export default withSiteContext(IndividualResource, {
  useGuard: true,
})
